import { useState, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router'
import {
  Box,
  Typography,
  Avatar,
  Chip,
  Paper,
  IconButton,
  Skeleton,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Drawer,
  Switch,
  FormControlLabel,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import ViewWeekIcon from '@mui/icons-material/ViewWeek'
import ViewDayIcon from '@mui/icons-material/ViewDay'
import CalendarViewMonthIcon from '@mui/icons-material/CalendarViewMonth'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import BusinessIcon from '@mui/icons-material/Business'
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import PrintIcon from '@mui/icons-material/Print'
import Person from '@mui/icons-material/Person'
import TimelineIcon from '@mui/icons-material/Timeline'
import LoginIcon from '@mui/icons-material/Login'
import LogoutIcon from '@mui/icons-material/Logout'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CloseIcon from '@mui/icons-material/Close'
import EventIcon from '@mui/icons-material/Event'
import RefreshIcon from '@mui/icons-material/Refresh'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import CancelIcon from '@mui/icons-material/Cancel'
import VisibilityIcon from '@mui/icons-material/Visibility'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd'
import PendingActionsIcon from '@mui/icons-material/PendingActions'
import dayjs from 'dayjs'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import { getPractitionerRoster, getMentorEmrUsageLogs } from 'src/supportPortal/services/rosterManagementService'
import { getSPDataWithParams } from 'src/supportPortal/services/services'
import {
  getDataMapFromCommonResponse,
  getImageLink,
  isCommonResponseSuccessfulV2,
} from 'src/mentor/helpers/utilityFunctions'
import './rosterPrintStyles.css'

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.03) 0%, rgba(139, 92, 246, 0.05) 50%, rgba(236, 72, 153, 0.03) 100%)',
  paddingBottom: theme.spacing(4),
}))

const ProfileCard = styled(Paper)(({ theme }) => ({
  margin: theme.spacing(3),
  marginBottom: theme.spacing(2),
  padding: theme.spacing(3),
  borderRadius: '20px',
  background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  boxShadow: '0 8px 32px rgba(99, 102, 241, 0.1), 0 2px 8px rgba(0, 0, 0, 0.05)',
  border: '1px solid rgba(255, 255, 255, 0.8)',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    textAlign: 'center',
    padding: theme.spacing(2),
    margin: theme.spacing(2),
  },
}))

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: 80,
  height: 80,
  border: '4px solid rgba(99, 102, 241, 0.2)',
  boxShadow: '0 4px 16px rgba(99, 102, 241, 0.2)',
  [theme.breakpoints.down('sm')]: {
    width: 64,
    height: 64,
  },
}))

const NavigationContainer = styled(Box)(({ theme }) => ({
  margin: theme.spacing(0, 3, 2, 3),
  padding: theme.spacing(2),
  borderRadius: '16px',
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.05)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    margin: theme.spacing(0, 2, 2, 2),
  },
}))

const ViewToggle = styled(ToggleButtonGroup)(({ theme }) => ({
  '& .MuiToggleButton-root': {
    border: 'none',
    borderRadius: '12px !important',
    padding: theme.spacing(1, 2),
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    '&.Mui-selected': {
      background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
      color: '#fff',
      boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)',
      '&:hover': {
        background: 'linear-gradient(135deg, #5558e3 0%, #7c4ee8 100%)',
      },
    },
    '&:hover': {
      background: 'rgba(99, 102, 241, 0.08)',
    },
  },
}))

const ContentContainer = styled(Box)(({ theme }) => ({
  margin: theme.spacing(0, 3),
  [theme.breakpoints.down('sm')]: {
    margin: theme.spacing(0, 2),
  },
}))

const DayCard = styled(Paper)(({ theme, isToday, isPast }) => ({
  borderRadius: '16px',
  overflow: 'hidden',
  background: isToday
    ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)'
    : isPast
    ? 'rgba(248, 250, 252, 0.9)'
    : 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  boxShadow: isToday
    ? '0 8px 24px rgba(99, 102, 241, 0.15), 0 2px 8px rgba(0, 0, 0, 0.05)'
    : '0 4px 16px rgba(0, 0, 0, 0.05)',
  border: isToday
    ? '2px solid rgba(99, 102, 241, 0.3)'
    : '1px solid rgba(0, 0, 0, 0.06)',
  transition: 'all 0.3s ease',
  opacity: isPast ? 0.7 : 1,
  '&:hover': {
    transform: isPast ? 'none' : 'translateY(-2px)',
    boxShadow: isPast
      ? '0 4px 16px rgba(0, 0, 0, 0.05)'
      : '0 8px 24px rgba(0, 0, 0, 0.1)',
  },
}))

const DayHeader = styled(Box)(({ theme, isToday }) => ({
  padding: theme.spacing(1.5, 2),
  background: isToday
    ? 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)'
    : 'linear-gradient(135deg, #64748b 0%, #475569 100%)',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}))

const SlotCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.5),
  margin: theme.spacing(1),
  borderRadius: '12px',
  background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.08) 100%)',
  border: '1px solid rgba(16, 185, 129, 0.2)',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.12) 0%, rgba(5, 150, 105, 0.12) 100%)',
    transform: 'scale(1.01)',
  },
}))

const NoSlotMessage = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  textAlign: 'center',
  color: theme.palette.text.secondary,
  fontStyle: 'italic',
  fontSize: '0.875rem',
}))

export default function PractitionerRosterView() {
  const { mentorSlug } = useParams()
  const navigate = useNavigate()
  
  const [viewType, setViewType] = useState('weekly')
  const [selectedDate, setSelectedDate] = useState(dayjs())
  const [selectedWeek, setSelectedWeek] = useState(dayjs().startOf('week'))
  const [selectedMonth, setSelectedMonth] = useState(dayjs().startOf('month'))
  const [loading, setLoading] = useState(true)
  const [mentorData, setMentorData] = useState(null)
  const [rosterData, setRosterData] = useState([])
  const [emrLogsData, setEmrLogsData] = useState([])
  const [emrLogsLoading, setEmrLogsLoading] = useState(false)
  const [timelineDialogOpen, setTimelineDialogOpen] = useState(false)
  const [timelineDate, setTimelineDate] = useState(null)
  const [showAllLogs, setShowAllLogs] = useState(false)
  const [allEmrLogsData, setAllEmrLogsData] = useState([])
  
  // Fetch mentor data
  useEffect(() => {
    const fetchMentorData = async () => {
      // Try to get from sessionStorage first
      const storedData = sessionStorage.getItem('supportMentorData')
      if (storedData) {
        try {
          const parsed = JSON.parse(storedData)
          if (parsed.slug === mentorSlug) {
            setMentorData(parsed)
            return
          }
        } catch (e) {
          console.error('Error parsing stored mentor data:', e)
        }
      }
      
      // Fetch from API if not in storage
      try {
        const resp = await getSPDataWithParams('/api/secure/cc/support/therapists/list')
        if (resp.status === 200 && isCommonResponseSuccessfulV2(resp)) {
          const mentors = getDataMapFromCommonResponse(resp)?.mentor || []
          const found = mentors.find(m => m.slug === mentorSlug)
          if (found) {
            setMentorData(found)
          }
        }
      } catch (error) {
        console.error('Error fetching mentor:', error)
      }
    }
    
    fetchMentorData()
  }, [mentorSlug])
  
  // Fetch roster data
  useEffect(() => {
    const fetchRosterData = async () => {
      if (!mentorData?.id) return
      
      setLoading(true)
      try {
        let fromDate, toDate
        
        if (viewType === 'daily') {
          fromDate = selectedDate.format('YYYY-MM-DD')
          toDate = selectedDate.format('YYYY-MM-DD')
        } else if (viewType === 'weekly') {
          fromDate = selectedWeek.startOf('week').format('YYYY-MM-DD')
          toDate = selectedWeek.endOf('week').format('YYYY-MM-DD')
        } else {
          // Monthly view
          fromDate = selectedMonth.startOf('month').format('YYYY-MM-DD')
          toDate = selectedMonth.endOf('month').format('YYYY-MM-DD')
        }
        
        const resp = await getPractitionerRoster({
          fromDate,
          toDate,
          mentorId: mentorData.id,
          includeBookedSlots: true,
        })
        
        if (resp.status === 200) {
          // Handle CommonResponse format
          const responseData = resp.data?.data?.rosterSlots || resp.data?.payload?.rosterSlots || resp.data?.rosterSlots || resp.data?.payload || resp.data?.data || resp.data || []
          setRosterData(Array.isArray(responseData) ? responseData : [])
        }
      } catch (error) {
        console.error('Error fetching roster:', error)
        setRosterData([])
      } finally {
        setLoading(false)
      }
    }
    
    fetchRosterData()
  }, [mentorData?.id, viewType, selectedDate, selectedWeek, selectedMonth])
  
  // Fetch EMR usage logs (check-in/check-out presence)
  useEffect(() => {
    const fetchEmrLogs = async () => {
      if (!mentorData?.id) return
      
      setEmrLogsLoading(true)
      try {
        let fromDate, toDate
        
        if (viewType === 'daily') {
          fromDate = selectedDate.format('YYYY-MM-DD')
          toDate = selectedDate.format('YYYY-MM-DD')
        } else if (viewType === 'weekly') {
          fromDate = selectedWeek.startOf('week').format('YYYY-MM-DD')
          toDate = selectedWeek.endOf('week').format('YYYY-MM-DD')
        } else {
          fromDate = selectedMonth.startOf('month').format('YYYY-MM-DD')
          toDate = selectedMonth.endOf('month').format('YYYY-MM-DD')
        }
        
        const resp = await getMentorEmrUsageLogs({
          fromDate,
          toDate,
          mentorId: mentorData.id,
          // Only fetch check-in/check-out/thrown-out events
          logEventIds: ['log-check-in', 'log-check-out', 'log-thrown-out'],
        })
        
        if (resp.status === 200) {
          const logsData = resp.data?.data?.usageLogs || resp.data?.usageLogs || []
          setEmrLogsData(Array.isArray(logsData) ? logsData : [])
        }
      } catch (error) {
        console.error('Error fetching EMR logs:', error)
        setEmrLogsData([])
      } finally {
        setEmrLogsLoading(false)
      }
    }
    
    fetchEmrLogs()
  }, [mentorData?.id, viewType, selectedDate, selectedWeek, selectedMonth])
  
  // Fetch all EMR logs for a specific date when drawer is open and showAllLogs is enabled
  useEffect(() => {
    const fetchAllLogsForDate = async () => {
      if (!mentorData?.id || !timelineDate || !showAllLogs) {
        setAllEmrLogsData([])
        return
      }
      
      try {
        const dateStr = timelineDate.format('YYYY-MM-DD')
        const resp = await getMentorEmrUsageLogs({
          fromDate: dateStr,
          toDate: dateStr,
          mentorId: mentorData.id,
          // No logEventIds filter - fetch all log types
        })
        
        if (resp.status === 200) {
          const logsData = resp.data?.data?.usageLogs || resp.data?.usageLogs || []
          setAllEmrLogsData(Array.isArray(logsData) ? logsData : [])
        }
      } catch (error) {
        console.error('Error fetching all EMR logs:', error)
        setAllEmrLogsData([])
      }
    }
    
    fetchAllLogsForDate()
  }, [mentorData?.id, timelineDate, showAllLogs])
  
  // Get week days for weekly view
  const weekDays = useMemo(() => {
    const days = []
    for (let i = 0; i < 7; i++) {
      days.push(selectedWeek.startOf('week').add(i, 'day'))
    }
    return days
  }, [selectedWeek])
  
  // Get month days for monthly view (calendar grid)
  const monthCalendar = useMemo(() => {
    const startOfMonth = selectedMonth.startOf('month')
    const endOfMonth = selectedMonth.endOf('month')
    const startOfCalendar = startOfMonth.startOf('week')
    const endOfCalendar = endOfMonth.endOf('week')
    
    const weeks = []
    let current = startOfCalendar
    
    while (current.isBefore(endOfCalendar) || current.isSame(endOfCalendar, 'day')) {
      const week = []
      for (let i = 0; i < 7; i++) {
        week.push(current)
        current = current.add(1, 'day')
      }
      weeks.push(week)
    }
    
    return weeks
  }, [selectedMonth])
  
  // Group roster slots by date
  const slotsByDate = useMemo(() => {
    const grouped = new Map()
    
    rosterData.forEach(slot => {
      // API returns customDate for ClinicRosterSlot
      const date = slot.customDate || slot.slotDate || slot.date
      if (date) {
        const existing = grouped.get(date) || []
        existing.push(slot)
        grouped.set(date, existing)
      }
    })
    
    // Sort slots within each date by start time
    grouped.forEach((slots, date) => {
      slots.sort((a, b) => {
        const timeA = a.slotStartTime || a.startTime || '00:00'
        const timeB = b.slotStartTime || b.startTime || '00:00'
        return timeA.localeCompare(timeB)
      })
    })
    
    return grouped
  }, [rosterData])
  
  // Calculate total allotted duration (in minutes, then format as hours:minutes)
  const totalAllottedDuration = useMemo(() => {
    let totalMinutes = 0
    
    rosterData.forEach(slot => {
      const startTime = slot.slotStartTime || slot.startTime
      const endTime = slot.slotEndTime || slot.endTime
      
      if (startTime && endTime) {
        const [startHour, startMin] = startTime.split(':').map(Number)
        const [endHour, endMin] = endTime.split(':').map(Number)
        
        const startTotalMins = startHour * 60 + startMin
        const endTotalMins = endHour * 60 + endMin
        
        // Handle case where end time might be on next day (unlikely for roster slots)
        const durationMins = endTotalMins >= startTotalMins 
          ? endTotalMins - startTotalMins 
          : (24 * 60 - startTotalMins) + endTotalMins
        
        totalMinutes += durationMins
      }
    })
    
    const hours = Math.floor(totalMinutes / 60)
    const minutes = totalMinutes % 60
    
    return {
      hours,
      minutes,
      totalMinutes,
      formatted: minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`,
    }
  }, [rosterData])
  
  // Group EMR logs by date
  const emrLogsByDate = useMemo(() => {
    const grouped = new Map()
    
    emrLogsData.forEach(log => {
      const date = log.logDate
      if (date) {
        const existing = grouped.get(date) || []
        existing.push(log)
        grouped.set(date, existing)
      }
    })
    
    // Sort logs within each date by time
    grouped.forEach((logs, date) => {
      logs.sort((a, b) => (a.logTime || '').localeCompare(b.logTime || ''))
    })
    
    return grouped
  }, [emrLogsData])
  
  // Calculate actual presence durations from check-in/check-out pairs
  const actualPresenceData = useMemo(() => {
    const presenceByDate = new Map()
    let totalActualMinutes = 0
    let incompleteCheckIns = []
    
    // Process each date's logs
    emrLogsByDate.forEach((logs, date) => {
      const checkIns = logs.filter(l => l.logEventId === 'log-check-in')
      const checkOuts = logs.filter(l => l.logEventId === 'log-check-out' || l.logEventId === 'log-thrown-out')
      
      const pairings = []
      const usedCheckOutIndices = new Set()
      
      // Pair each check-in with the next check-out
      checkIns.forEach(checkIn => {
        const checkInTime = checkIn.logTime
        
        // Find the next check-out after this check-in (same chamber room if possible)
        let matchingCheckOut = null
        let matchingIndex = -1
        
        for (let i = 0; i < checkOuts.length; i++) {
          if (usedCheckOutIndices.has(i)) continue
          
          const checkOut = checkOuts[i]
          if (checkOut.logTime > checkInTime) {
            // Prefer same chamber room
            if (checkOut.chamberRoomId === checkIn.chamberRoomId) {
              matchingCheckOut = checkOut
              matchingIndex = i
              break
            }
            // Otherwise use first available
            if (!matchingCheckOut) {
              matchingCheckOut = checkOut
              matchingIndex = i
            }
          }
        }
        
        if (matchingCheckOut) {
          usedCheckOutIndices.add(matchingIndex)
          
          // Calculate duration
          const [inHour, inMin] = checkInTime.split(':').map(Number)
          const outTime = matchingCheckOut.logTime
          const [outHour, outMin] = outTime.split(':').map(Number)
          
          const inTotalMins = inHour * 60 + inMin
          const outTotalMins = outHour * 60 + outMin
          const durationMins = outTotalMins >= inTotalMins ? outTotalMins - inTotalMins : 0
          
          totalActualMinutes += durationMins
          
          pairings.push({
            checkIn,
            checkOut: matchingCheckOut,
            durationMins,
            chamberRoomId: checkIn.chamberRoomId,
            chamberRoomTitle: checkIn.chamberRoomTitle,
            clinicBrandName: checkIn.clinicBrandName,
          })
        } else {
          // No check-out found - incomplete
          incompleteCheckIns.push({
            ...checkIn,
            date,
          })
        }
      })
      
      if (pairings.length > 0) {
        presenceByDate.set(date, pairings)
      }
    })
    
    const hours = Math.floor(totalActualMinutes / 60)
    const minutes = totalActualMinutes % 60
    
    return {
      presenceByDate,
      totalActualMinutes,
      totalFormatted: minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`,
      incompleteCheckIns,
    }
  }, [emrLogsByDate])
  
  // Calculate roster allotted minutes per date for percentage calculation
  const rosterMinutesByDate = useMemo(() => {
    const minutesByDate = new Map()
    
    rosterData.forEach(slot => {
      const date = slot.customDate || slot.slotDate || slot.date
      const startTime = slot.slotStartTime || slot.startTime
      const endTime = slot.slotEndTime || slot.endTime
      
      if (date && startTime && endTime) {
        const [startHour, startMin] = startTime.split(':').map(Number)
        const [endHour, endMin] = endTime.split(':').map(Number)
        
        const startTotalMins = startHour * 60 + startMin
        const endTotalMins = endHour * 60 + endMin
        const durationMins = endTotalMins >= startTotalMins ? endTotalMins - startTotalMins : 0
        
        const existing = minutesByDate.get(date) || 0
        minutesByDate.set(date, existing + durationMins)
      }
    })
    
    return minutesByDate
  }, [rosterData])
  
  // Calculate overall percentage
  const overallPercentage = useMemo(() => {
    if (totalAllottedDuration.totalMinutes === 0) return 0
    return Math.round((actualPresenceData.totalActualMinutes / totalAllottedDuration.totalMinutes) * 100)
  }, [actualPresenceData.totalActualMinutes, totalAllottedDuration.totalMinutes])
  
  // Open timeline dialog for a specific date
  const handleOpenTimeline = (date) => {
    setTimelineDate(date)
    setTimelineDialogOpen(true)
  }
  
  // Get log event display info
  const getLogEventInfo = (logEventId) => {
    const logEventMap = {
      'log-check-in': { title: 'Check-In', icon: 'login', color: '#10b981' },
      'log-check-in-refresh': { title: 'Check-In Refresh', icon: 'refresh', color: '#0ea5e9' },
      'log-check-out': { title: 'Check-Out', icon: 'logout', color: '#f59e0b' },
      'log-thrown-out': { title: 'Thrown Out', icon: 'warning', color: '#ef4444' },
      'log-check-in-request': { title: 'Check-In Request', icon: 'request', color: '#8b5cf6' },
      'log-check-in-request-ack': { title: 'Request Approved', icon: 'approved', color: '#10b981' },
      'log-check-in-request-denied': { title: 'Request Denied', icon: 'denied', color: '#ef4444' },
      're-check-in': { title: 'Re-Check-In', icon: 'login', color: '#0891b2' },
      'log-screen-idle': { title: 'Screen Idle', icon: 'idle', color: '#94a3b8' },
      'enter-without-checkin': { title: 'Enter Without Check-In', icon: 'peek', color: '#f97316' },
      'log-peek-in-refresh': { title: 'Peek-In Refresh', icon: 'refresh', color: '#64748b' },
      'audit-checkin': { title: 'Auditor Check-In', icon: 'audit', color: '#6366f1' },
      'audit-checkout': { title: 'Auditor Check-Out', icon: 'audit', color: '#8b5cf6' },
    }
    return logEventMap[logEventId] || { title: logEventId, icon: 'default', color: '#64748b' }
  }
  
  // Get combined timeline events for a date
  const getTimelineEvents = (date) => {
    const dateStr = dayjs(date).format('YYYY-MM-DD')
    const events = []
    
    // Add roster slots
    const slots = slotsByDate.get(dateStr) || []
    slots.forEach(slot => {
      events.push({
        type: 'roster-start',
        time: slot.slotStartTime,
        title: 'Roster Start',
        subtitle: `${slot.chamberRoomTitle} @ ${slot.clinicBrandName}`,
        icon: 'event',
        color: '#6366f1',
      })
      events.push({
        type: 'roster-end',
        time: slot.slotEndTime,
        title: 'Roster End',
        subtitle: `${slot.chamberRoomTitle}`,
        icon: 'event',
        color: '#6366f1',
      })
    })
    
    // Add EMR logs - use all logs if showAllLogs is enabled, otherwise use filtered logs
    const logs = showAllLogs 
      ? allEmrLogsData.filter(l => l.logDate === dateStr).sort((a, b) => (a.logTime || '').localeCompare(b.logTime || ''))
      : (emrLogsByDate.get(dateStr) || [])
    
    logs.forEach(log => {
      const info = getLogEventInfo(log.logEventId)
      
      events.push({
        type: log.logEventId,
        time: log.logTime?.substring(0, 5) || '--:--',
        title: info.title,
        subtitle: `${log.chamberRoomTitle || 'Unknown Room'} @ ${log.clinicBrandName || 'Clinic'}`,
        icon: info.icon,
        color: info.color,
        checkinMethod: log.checkinMethod,
        logEventId: log.logEventId,
      })
    })
    
    // Sort by time
    events.sort((a, b) => (a.time || '').localeCompare(b.time || ''))
    
    return events
  }
  
  const handleViewChange = (event, newView) => {
    if (newView) setViewType(newView)
  }
  
  const handlePrevious = () => {
    if (viewType === 'daily') {
      setSelectedDate(selectedDate.subtract(1, 'day'))
    } else if (viewType === 'weekly') {
      setSelectedWeek(selectedWeek.subtract(1, 'week'))
    } else {
      setSelectedMonth(selectedMonth.subtract(1, 'month'))
    }
  }
  
  const handleNext = () => {
    if (viewType === 'daily') {
      setSelectedDate(selectedDate.add(1, 'day'))
    } else if (viewType === 'weekly') {
      setSelectedWeek(selectedWeek.add(1, 'week'))
    } else {
      setSelectedMonth(selectedMonth.add(1, 'month'))
    }
  }
  
  // Get display title for navigation
  const getNavigationTitle = () => {
    if (viewType === 'daily') {
      return selectedDate.format('dddd, MMMM D, YYYY')
    } else if (viewType === 'weekly') {
      return `${selectedWeek.startOf('week').format('MMM D')} - ${selectedWeek.endOf('week').format('MMM D, YYYY')}`
    } else {
      return selectedMonth.format('MMMM YYYY')
    }
  }
  
  const handlePrint = () => {
    window.print()
  }
  
  const renderSlot = (slot) => {
    const startTime = slot.slotStartTime || slot.startTime || '--:--'
    const endTime = slot.slotEndTime || slot.endTime || '--:--'
    const clinicBrand = slot.clinicBrandName || slot.clinicBrand?.name || 'Unknown Clinic'
    const clinicLocation = slot.clinicLocationName || slot.clinicLocation?.name || ''
    const roomTitle = slot.chamberRoomTitle || slot.chamberRoom?.title || 'Room'
    
    return (
      <SlotCard key={slot.id || `${slot.slotDate}-${startTime}`}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <AccessTimeIcon sx={{ fontSize: '1rem', color: 'success.main' }} />
          <Typography variant="body2" sx={{ fontWeight: 700, color: 'success.dark' }}>
            {startTime} - {endTime}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <BusinessIcon sx={{ fontSize: '0.875rem', color: 'text.secondary' }} />
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>
            {clinicBrand}
          </Typography>
        </Box>
        {clinicLocation && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <LocationOnIcon sx={{ fontSize: '0.875rem', color: 'text.secondary' }} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {clinicLocation}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MeetingRoomIcon sx={{ fontSize: '0.875rem', color: 'text.secondary' }} />
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {roomTitle}
          </Typography>
        </Box>
      </SlotCard>
    )
  }
  
  const renderDayCard = (day) => {
    const dateStr = day.format('YYYY-MM-DD')
    const isToday = day.isSame(dayjs(), 'day')
    const isPast = day.isBefore(dayjs(), 'day')
    const slots = slotsByDate.get(dateStr) || []
    const presencePairs = actualPresenceData.presenceByDate.get(dateStr) || []
    const dayLogs = emrLogsByDate.get(dateStr) || []
    const hasEmrData = dayLogs.length > 0
    
    // Calculate actual duration for this day
    const actualDayMins = presencePairs.reduce((sum, p) => sum + p.durationMins, 0)
    const actualDayHours = Math.floor(actualDayMins / 60)
    const actualDayMinRemainder = actualDayMins % 60
    const actualDayFormatted = actualDayMins > 0 
      ? (actualDayMinRemainder > 0 ? `${actualDayHours}h ${actualDayMinRemainder}m` : `${actualDayHours}h`)
      : null
    
    // Get rostered minutes for this day and calculate percentage
    const rosteredDayMins = rosterMinutesByDate.get(dateStr) || 0
    const dayPercentage = rosteredDayMins > 0 ? Math.round((actualDayMins / rosteredDayMins) * 100) : 0
    
    // Check for incomplete check-ins on this day
    const incompleteForDay = actualPresenceData.incompleteCheckIns.filter(c => c.date === dateStr)
    
    return (
      <DayCard key={dateStr} isToday={isToday} isPast={isPast}>
        <DayHeader isToday={isToday}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
              {day.format('dddd')}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {day.format('MMM D, YYYY')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {isToday && (
              <Chip
                label="Today"
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                }}
              />
            )}
            {hasEmrData && (
              <Tooltip title="View Timeline">
                <IconButton
                  size="small"
                  onClick={() => handleOpenTimeline(day)}
                  sx={{ color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff' } }}
                >
                  <TimelineIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </DayHeader>
        
        {/* Actual Presence Summary */}
        {(actualDayFormatted || incompleteForDay.length > 0) && (
          <Box sx={{ 
            mx: 1, 
            mt: 1, 
            p: 1, 
            borderRadius: '8px', 
            background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
            border: '1px solid rgba(16, 185, 129, 0.2)',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <LoginIcon sx={{ fontSize: '0.875rem', color: '#10b981' }} />
                <Typography variant="caption" sx={{ fontWeight: 600, color: '#059669' }}>
                  Actual: {actualDayFormatted || '0h'}
                  {rosteredDayMins > 0 && (
                    <Typography 
                      component="span" 
                      variant="caption" 
                      sx={{ 
                        ml: 0.5, 
                        fontWeight: 700,
                        color: dayPercentage >= 90 ? '#10b981' : dayPercentage >= 70 ? '#d97706' : '#ef4444',
                      }}
                    >
                      ({dayPercentage}%)
                    </Typography>
                  )}
                </Typography>
              </Box>
              {incompleteForDay.length > 0 && (
                <Tooltip title={`${incompleteForDay.length} incomplete check-in(s) - no checkout found`}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <WarningAmberIcon sx={{ fontSize: '0.875rem', color: '#f59e0b' }} />
                    <Typography variant="caption" sx={{ color: '#d97706' }}>
                      {incompleteForDay.length}
                    </Typography>
                  </Box>
                </Tooltip>
              )}
            </Box>
          </Box>
        )}
        
        <Box sx={{ minHeight: 100 }}>
          {loading ? (
            <Box sx={{ p: 2 }}>
              <Skeleton variant="rounded" height={60} sx={{ mb: 1 }} />
              <Skeleton variant="rounded" height={60} />
            </Box>
          ) : slots.length > 0 ? (
            slots.map(slot => renderSlot(slot))
          ) : (
            <NoSlotMessage>
              {isPast ? 'No assignments' : 'No scheduled slots'}
            </NoSlotMessage>
          )}
        </Box>
      </DayCard>
    )
  }
  
  return (
    <PageContainer className="practitioner-roster-view">
      <SupportHeader 
        heading={`${mentorData?.name || 'Practitioner'}'s Roster`}
        showBackButton
      />
      
      {/* Profile Card */}
      <ProfileCard className="no-print">
        <StyledAvatar
          src={mentorData?.profilePicId ? getImageLink(mentorData.profilePicId) : null}
        >
          <Person sx={{ fontSize: 40 }} />
        </StyledAvatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
            {mentorData?.name || mentorData?.fullName || 'Loading...'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
            {mentorData?.email || mentorData?.slug || ''}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {mentorData?.specializations?.map((spec, idx) => (
              <Chip
                key={idx}
                label={spec}
                size="small"
                sx={{
                  bgcolor: 'rgba(99, 102, 241, 0.1)',
                  color: '#6366f1',
                  fontWeight: 500,
                }}
              />
            ))}
          </Box>
        </Box>
        <Tooltip title="Print Roster">
          <IconButton
            onClick={handlePrint}
            sx={{
              bgcolor: 'rgba(99, 102, 241, 0.1)',
              color: '#6366f1',
              '&:hover': {
                bgcolor: 'rgba(99, 102, 241, 0.2)',
              },
            }}
          >
            <PrintIcon />
          </IconButton>
        </Tooltip>
      </ProfileCard>
      
      {/* Navigation */}
      <NavigationContainer className="no-print">
        <ViewToggle
          value={viewType}
          exclusive
          onChange={handleViewChange}
          size="small"
        >
          <ToggleButton value="daily">
            <ViewDayIcon sx={{ mr: 1, fontSize: '1.1rem' }} />
            Daily
          </ToggleButton>
          <ToggleButton value="weekly">
            <ViewWeekIcon sx={{ mr: 1, fontSize: '1.1rem' }} />
            Weekly
          </ToggleButton>
          <ToggleButton value="monthly">
            <CalendarViewMonthIcon sx={{ mr: 1, fontSize: '1.1rem' }} />
            Monthly
          </ToggleButton>
        </ViewToggle>
        
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={handlePrevious}
            sx={{
              bgcolor: 'rgba(0,0,0,0.05)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CalendarTodayIcon sx={{ color: 'text.secondary', fontSize: '1.2rem' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, minWidth: 240, textAlign: 'center' }}>
              {getNavigationTitle()}
            </Typography>
          </Box>
          
          <IconButton
            onClick={handleNext}
            sx={{
              bgcolor: 'rgba(0,0,0,0.05)',
              '&:hover': { bgcolor: 'rgba(0,0,0,0.1)' },
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
        
        <Box sx={{ width: { xs: '100%', sm: 'auto' } }} />
      </NavigationContainer>
      
      {/* Content */}
      <ContentContainer>
        {viewType === 'daily' ? (
          <Box sx={{ maxWidth: 500, mx: 'auto' }}>
            {renderDayCard(selectedDate)}
          </Box>
        ) : viewType === 'weekly' ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)',
                lg: 'repeat(7, 1fr)',
              },
              gap: 2,
            }}
          >
            {weekDays.map(day => renderDayCard(day))}
          </Box>
        ) : (
          /* Monthly Calendar View */
          <Paper
            sx={{
              borderRadius: '20px',
              overflow: 'hidden',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
            }}
          >
            {/* Calendar Header - Day Names */}
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(7, 1fr)',
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                py: 1.5,
              }}
            >
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <Typography
                  key={day}
                  sx={{
                    textAlign: 'center',
                    fontWeight: 700,
                    color: '#fff',
                    fontSize: '0.875rem',
                  }}
                >
                  {day}
                </Typography>
              ))}
            </Box>
            
            {/* Calendar Grid */}
            {monthCalendar.map((week, weekIdx) => (
              <Box
                key={weekIdx}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  borderTop: weekIdx > 0 ? '1px solid rgba(0, 0, 0, 0.08)' : 'none',
                }}
              >
                {week.map((day) => {
                  const dateStr = day.format('YYYY-MM-DD')
                  const isCurrentMonth = day.month() === selectedMonth.month()
                  const isToday = day.isSame(dayjs(), 'day')
                  const isPast = day.isBefore(dayjs(), 'day')
                  const slots = slotsByDate.get(dateStr) || []
                  const hasSlots = slots.length > 0
                  const dayLogs = emrLogsByDate.get(dateStr) || []
                  const hasEmrData = dayLogs.length > 0
                  const presencePairs = actualPresenceData.presenceByDate.get(dateStr) || []
                  const actualDayMins = presencePairs.reduce((sum, p) => sum + p.durationMins, 0)
                  const rosteredDayMins = rosterMinutesByDate.get(dateStr) || 0
                  const dayPercentage = rosteredDayMins > 0 ? Math.round((actualDayMins / rosteredDayMins) * 100) : 0
                  
                  return (
                    <Box
                      key={dateStr}
                      sx={{
                        minHeight: 100,
                        p: 1,
                        borderRight: '1px solid rgba(0, 0, 0, 0.05)',
                        '&:last-child': { borderRight: 'none' },
                        opacity: isCurrentMonth ? 1 : 0.4,
                        background: isToday
                          ? 'rgba(99, 102, 241, 0.08)'
                          : isPast && isCurrentMonth
                          ? 'rgba(0, 0, 0, 0.02)'
                          : 'transparent',
                        cursor: hasEmrData ? 'pointer' : 'default',
                        '&:hover': hasEmrData ? { background: 'rgba(99, 102, 241, 0.05)' } : {},
                      }}
                      onClick={() => hasEmrData && handleOpenTimeline(day)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography
                          sx={{
                            fontWeight: isToday ? 700 : 500,
                            fontSize: '0.875rem',
                            color: isToday ? '#6366f1' : 'text.primary',
                          }}
                        >
                          {day.format('D')}
                          {isToday && (
                            <Chip
                              label="Today"
                              size="small"
                              sx={{
                                ml: 0.5,
                                height: '18px',
                                fontSize: '0.625rem',
                                bgcolor: '#6366f1',
                                color: '#fff',
                              }}
                            />
                          )}
                        </Typography>
                        {hasEmrData && (
                          <TimelineIcon sx={{ fontSize: '0.75rem', color: '#6366f1', opacity: 0.6 }} />
                        )}
                      </Box>
                      
                      {/* Actual presence indicator */}
                      {actualDayMins > 0 && (
                        <Box sx={{
                          px: 0.5,
                          py: 0.25,
                          mb: 0.5,
                          borderRadius: '4px',
                          background: dayPercentage >= 90 ? 'rgba(16, 185, 129, 0.1)' : dayPercentage >= 70 ? 'rgba(245, 158, 11, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                          border: `1px solid ${dayPercentage >= 90 ? 'rgba(16, 185, 129, 0.3)' : dayPercentage >= 70 ? 'rgba(245, 158, 11, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`,
                          fontSize: '0.55rem',
                          fontWeight: 600,
                          color: dayPercentage >= 90 ? '#059669' : dayPercentage >= 70 ? '#d97706' : '#dc2626',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.25,
                        }}>
                          <LoginIcon sx={{ fontSize: '0.55rem' }} />
                          {Math.floor(actualDayMins / 60)}h{actualDayMins % 60 > 0 ? `${actualDayMins % 60}m` : ''}
                          {rosteredDayMins > 0 && (
                            <Typography component="span" sx={{ fontSize: '0.55rem', fontWeight: 700, ml: 0.25 }}>
                              ({dayPercentage}%)
                            </Typography>
                          )}
                        </Box>
                      )}
                      
                      {loading ? (
                        <Skeleton variant="rounded" height={24} />
                      ) : hasSlots ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {slots.slice(0, 2).map((slot, idx) => (
                            <Tooltip
                              key={idx}
                              title={`${slot.slotStartTime || '--:--'} - ${slot.slotEndTime || '--:--'} @ ${slot.clinicBrandName || 'Clinic'}`}
                            >
                              <Box
                                sx={{
                                  px: 0.75,
                                  py: 0.25,
                                  borderRadius: '6px',
                                  background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
                                  border: '1px solid rgba(99, 102, 241, 0.3)',
                                  fontSize: '0.65rem',
                                  fontWeight: 600,
                                  color: '#6366f1',
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {slot.slotStartTime || '--:--'} - {slot.slotEndTime || '--:--'}
                              </Box>
                            </Tooltip>
                          ))}
                          {slots.length > 2 && (
                            <Typography
                              sx={{
                                fontSize: '0.65rem',
                                color: 'text.secondary',
                                fontStyle: 'italic',
                              }}
                            >
                              +{slots.length - 2} more
                            </Typography>
                          )}
                        </Box>
                      ) : isCurrentMonth && !isPast ? (
                        <Typography
                          sx={{
                            fontSize: '0.7rem',
                            color: 'text.disabled',
                            fontStyle: 'italic',
                          }}
                        >
                          No slots
                        </Typography>
                      ) : null}
                    </Box>
                  )
                })}
              </Box>
            ))}
          </Paper>
        )}
        
        {/* Summary Stats */}
        {!loading && (
          <Paper
            sx={{
              mt: 3,
              p: 3,
              borderRadius: '16px',
              background: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              Summary
            </Typography>
            <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#6366f1' }}>
                  {rosterData.length}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Total Slots
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#8b5cf6' }}>
                  {totalAllottedDuration.formatted}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Allotted Duration
                </Typography>
              </Box>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography variant="h4" sx={{ fontWeight: 700, color: '#10b981' }}>
                    {actualPresenceData.totalFormatted}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: 600, 
                      color: overallPercentage >= 90 ? '#10b981' : overallPercentage >= 70 ? '#f59e0b' : '#ef4444',
                    }}
                  >
                    ({overallPercentage}%)
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Actual Check-in vs Roster
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#f59e0b' }}>
                  {actualPresenceData.incompleteCheckIns.length}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Incomplete Check-ins
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#64748b' }}>
                  {new Set(rosterData.map(s => s.clinicBrandName || s.clinicBrand?.name).filter(Boolean)).size}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Clinics
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: '#94a3b8' }}>
                  {new Set(rosterData.map(s => s.chamberRoomId).filter(Boolean)).size}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Rooms
                </Typography>
              </Box>
            </Box>
            
            {/* Incomplete Check-ins List */}
            {actualPresenceData.incompleteCheckIns.length > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#d97706', mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <WarningAmberIcon sx={{ fontSize: '1rem' }} />
                  Incomplete Check-ins (No Checkout Found)
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {actualPresenceData.incompleteCheckIns.map((item, idx) => (
                    <Chip
                      key={idx}
                      size="small"
                      label={`${item.date} ${item.logTime?.substring(0, 5)} @ ${item.chamberRoomTitle || 'Room'}`}
                      sx={{ 
                        bgcolor: 'rgba(245, 158, 11, 0.1)', 
                        color: '#d97706',
                        border: '1px solid rgba(245, 158, 11, 0.3)',
                      }}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Paper>
        )}
      </ContentContainer>
      
      {/* Timeline Comparison Drawer */}
      <Drawer
        anchor="right"
        open={timelineDialogOpen}
        onClose={() => {
          setTimelineDialogOpen(false)
          setShowAllLogs(false)
        }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 420 },
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(20px)',
          },
        }}
      >
        {/* Drawer Header */}
        <Box sx={{ 
          p: 2,
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TimelineIcon />
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Timeline - {timelineDate?.format('MMM D, YYYY')}
            </Typography>
          </Box>
          <IconButton 
            onClick={() => {
              setTimelineDialogOpen(false)
              setShowAllLogs(false)
            }} 
            sx={{ color: '#fff' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        {/* Toggle for showing all logs */}
        <Box sx={{ 
          px: 2, 
          py: 1.5, 
          borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
          background: 'rgba(99, 102, 241, 0.05)',
        }}>
          <FormControlLabel
            control={
              <Switch
                checked={showAllLogs}
                onChange={(e) => setShowAllLogs(e.target.checked)}
                size="small"
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: '#6366f1',
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: '#6366f1',
                  },
                }}
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Show all check-in logs
              </Typography>
            }
          />
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', ml: 5 }}>
            {showAllLogs ? 'Showing all log events (refresh, idle, peek, etc.)' : 'Showing only check-in/check-out events'}
          </Typography>
        </Box>
        
        {/* Timeline Content */}
        <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
          {timelineDate && (
            <Box sx={{ position: 'relative', pl: 4 }}>
              {/* Timeline line */}
              <Box sx={{
                position: 'absolute',
                left: 12,
                top: 0,
                bottom: 0,
                width: 2,
                background: 'linear-gradient(to bottom, #e2e8f0, #94a3b8)',
                borderRadius: 1,
              }} />
              
              {getTimelineEvents(timelineDate).map((event, idx) => (
                <Box key={idx} sx={{ position: 'relative', mb: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                  {/* Timeline dot */}
                  <Box sx={{
                    position: 'absolute',
                    left: -28,
                    width: 24,
                    height: 24,
                    borderRadius: '50%',
                    bgcolor: event.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    boxShadow: `0 2px 8px ${event.color}40`,
                  }}>
                    {event.icon === 'login' && <LoginIcon sx={{ fontSize: '0.875rem' }} />}
                    {event.icon === 'logout' && <LogoutIcon sx={{ fontSize: '0.875rem' }} />}
                    {event.icon === 'warning' && <WarningAmberIcon sx={{ fontSize: '0.875rem' }} />}
                    {event.icon === 'event' && <EventIcon sx={{ fontSize: '0.875rem' }} />}
                    {event.icon === 'refresh' && <RefreshIcon sx={{ fontSize: '0.875rem' }} />}
                    {event.icon === 'approved' && <CheckCircleIcon sx={{ fontSize: '0.875rem' }} />}
                    {event.icon === 'denied' && <CancelIcon sx={{ fontSize: '0.875rem' }} />}
                    {event.icon === 'request' && <PendingActionsIcon sx={{ fontSize: '0.875rem' }} />}
                    {event.icon === 'peek' && <VisibilityIcon sx={{ fontSize: '0.875rem' }} />}
                    {event.icon === 'idle' && <HourglassEmptyIcon sx={{ fontSize: '0.875rem' }} />}
                    {event.icon === 'audit' && <AssignmentIndIcon sx={{ fontSize: '0.875rem' }} />}
                    {event.icon === 'default' && <EventIcon sx={{ fontSize: '0.875rem' }} />}
                  </Box>
                  
                  {/* Event content */}
                  <Box sx={{ 
                    flex: 1, 
                    p: 1.5, 
                    borderRadius: '10px',
                    background: `${event.color}10`,
                    border: `1px solid ${event.color}30`,
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: event.color }}>
                        {event.title}
                      </Typography>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.primary' }}>
                        {event.time}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {event.subtitle}
                    </Typography>
                    {event.checkinMethod && (
                      <Chip 
                        label={event.checkinMethod === 'check-in-method-in-person' ? 'In-Person' : 'Remote'}
                        size="small"
                        sx={{ ml: 1, height: 18, fontSize: '0.65rem' }}
                      />
                    )}
                  </Box>
                </Box>
              ))}
              
              {getTimelineEvents(timelineDate).length === 0 && (
                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', py: 2 }}>
                  No events for this day
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Drawer>
    </PageContainer>
  )
}
